// Bottom-pinned sticky drawer + side-by-side comparison modal.
//
// Drawer renders the count + thumbnails + Compare/Clear actions; modal
// renders the actual 2–4 column comparison grid. Both subscribe to
// `useCompareStash()`. Drawer auto-hides when stash is empty.
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  X,
  GitCompareArrows,
  ChevronDown,
  ChevronUp,
  Trash2,
  Star,
  Award,
  CheckCircle2,
  Share2,
  Mail,
  Banknote,
  ShieldCheck,
  Wrench,
  Download,
  Printer,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useCompareStash } from "../lib/compareStash";
import { useQuoteModal } from "../lib/quoteModal";
import { track, events } from "../lib/analytics";
import { getInsurancePremiumByState } from "../lib/api";
import { statePremium } from "../lib/carrierStateMatrix";
import { CompareMobileCards } from "./CompareMobileCards";
import { ensureProvidersManifest, getProviderBySlug } from "../lib/providersManifest";
import AffiliateDisclosure from "./AffiliateDisclosure";
import CompareFreshnessStamp from "./CompareFreshnessStamp";
import {
  CompareWinnerRailPill,
  CompareWinnerBanner,
  pickCompareWinner,
} from "./CompareWinnerPill";
import {
  DETAILS_COMPARE_KEYS,
  detailLabel,
  fmtDetail,
  pickDetailWinners,
  pickUniversalWinners,
} from "../lib/providerDetails";

const CATEGORY_LABEL = {
  insurance: "Insurance",
  "auto-loan": "Auto loan",
  refinance: "Refinance",
  warranty: "Warranty",
};

// Iter-107 — Category-aware compare engine.
//
// The modal now switches its entire copy stack (eyebrow, title noun,
// intro sentence, vertical icon) based on the stash's category, and
// renders ONLY the universal spec rows that make sense for that
// vertical:
//   * Auto loan / refinance → keep APR + Min credit (loan-specific)
//   * Insurance / warranty  → drop APR/Min credit; lean on
//                             iter-94's per-category `detailRows`
//                             (avg premium, deductible, mileage cap, etc.)
// All four verticals share: Editor rating, Best for, Editor pick — those
// are universal editorial dimensions.
//
// `CATEGORY_COPY` is the single source of truth. Any new category just
// adds one entry here + a `DETAILS_COMPARE_KEYS` slot in providerDetails.js.
const CATEGORY_COPY = {
  insurance: {
    Icon: ShieldCheck,
    noun: "carriers",
    titleSuffix: "side-by-side",
    intro:
      "National average premiums, AM Best financial strength, and who each carrier fits best. Your quote depends on your driver profile and ZIP.",
  },
  "auto-loan": {
    Icon: Banknote,
    noun: "lenders",
    titleSuffix: "head-to-head",
    intro:
      "APR ranges, credit floors, refinance support, and what each lender is best for.",
  },
  refinance: {
    Icon: Banknote,
    noun: "refinance lenders",
    titleSuffix: "head-to-head",
    intro:
      "APR drops, monthly savings, funding speed, and what each refinance lender is best for.",
  },
  warranty: {
    Icon: Wrench,
    noun: "warranty providers",
    titleSuffix: "compared",
    intro:
      "Coverage levels, deductibles, mileage caps, and what each provider is best for.",
  },
};

const DEFAULT_COPY = {
  Icon: GitCompareArrows,
  noun: "providers",
  titleSuffix: "compared",
  intro: "Editor scores and what each provider is best for — all in one view.",
};

// Categories where APR + Min credit are universally relevant. For the
// other verticals (insurance, warranty) those rows would be either
// blank or misleading, so we hide them entirely. Iter-94's
// `detailRows` mechanism then surfaces the right per-vertical fields.
const LOAN_LIKE_CATEGORIES = new Set(["auto-loan", "refinance"]);

function fmtApr(min, max) {
  if (min == null && max == null) return "—";
  if (max == null || max === min) return `${Number(min).toFixed(2)}%`;
  return `${Number(min).toFixed(2)}%–${Number(max).toFixed(2)}%`;
}


// Iter-96 — Share button for the compare modal footer. Copies a URL of the
// form `{origin}/compare?stash=slug1,slug2,...` to the clipboard and toasts
// success. Falls back to a select-all prompt textarea for browsers without
// the Clipboard API (mostly old Safari + http localhost). Tracks the share
// event with category + slug count so we can measure viral-loop strength
// per vertical.
//
// Iter-196.W (C7 from audit) — Added a second "Email this comparison"
// button that opens the OS mail client with a pre-filled subject + body
// summarizing the stash (category, lender names, link). Pure mailto:
// — no API, no email-collection form, works on every device.
function ShareCompareButton({ items }) {
  const handleShare = async () => {
    if (typeof window === "undefined" || items.length === 0) return;
    const slugs = items.map((it) => it.slug).join(",");
    const url = `${window.location.origin}/compare?stash=${slugs}`;
    track(events.COMPARE_STASH_SHARE, {
      count: items.length,
      category: items[0]?.category,
      slugs,
    });
    // Clipboard API path
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Comparison link copied — share it anywhere.");
        return;
      } catch {
        // fall through to legacy path
      }
    }
    // Legacy fallback — show the URL in a prompt so the user can copy it
    try {
      window.prompt("Copy this comparison link:", url);
    } catch {
      toast.error("Couldn't copy the link. Try selecting the URL bar.");
    }
  };

  // Iter-196.W (C7) — `mailto:` handler. Pre-fills subject + body with
  // a 1-paragraph summary of the comparison + the share URL so the
  // recipient can land on /compare?stash=... and see the same modal.
  // No external integration — opens whichever mail client the user
  // has configured. Tracks separately from "copy link" so we can
  // measure email-share strength independently.
  const handleEmail = () => {
    if (typeof window === "undefined" || items.length === 0) return;
    const slugs = items.map((it) => it.slug).join(",");
    const url = `${window.location.origin}/compare?stash=${slugs}`;
    const cat = items[0]?.category || "auto";
    const catLabel = CATEGORY_LABEL[cat] || "Auto";
    const names = items.map((it) => it.name).join(" vs ");
    const subject = `${catLabel} comparison — ${names}`;
    const lines = [
      `Take a look at this side-by-side ${catLabel.toLowerCase()} comparison I pulled together on CarSavr:`,
      "",
      ...items.map(
        (it, i) =>
          `${i + 1}. ${it.name}${it.rating ? ` — ${Number(it.rating).toFixed(1)}/5 editor rating` : ""}`,
      ),
      "",
      `Open the full side-by-side here:`,
      url,
      "",
      "— sent via CarSavr.com",
    ];
    const body = lines.join("\n");
    track(events.COMPARE_STASH_EMAIL || "compare_stash_email", {
      count: items.length,
      category: items[0]?.category,
      slugs,
    });
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Use location.href so iOS Safari opens Mail.app reliably (window.open
    // sometimes opens a blank tab on iOS instead of handing off).
    window.location.href = mailtoUrl;
  };

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleShare}
        data-testid="compare-modal-share"
        className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 font-bold h-11 px-4 text-sm transition-colors"
        title="Copy a link that opens this same comparison"
      >
        <Share2 className="w-4 h-4" />
        Share link
      </button>
      <button
        type="button"
        onClick={handleEmail}
        data-testid="compare-modal-email"
        className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 font-bold h-11 px-4 text-sm transition-colors"
        title="Email this comparison to yourself or someone else"
      >
        <Mail className="w-4 h-4" />
        Email
      </button>
    </span>
  );
}

export function StickyCompareDrawer() {
  const {
    items,
    remove,
    clear,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    openModal,
  } = useCompareStash();

  if (items.length === 0) return null;

  return (
    <>
      {/* Minimized rail when drawer is closed */}
      {!isDrawerOpen && (
        <button
          type="button"
          data-testid="compare-drawer-rail"
          onClick={openDrawer}
          className="fixed bottom-5 right-5 z-[110] inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-bold h-12 px-4 shadow-2xl transition-all hover:-translate-y-0.5"
        >
          <GitCompareArrows className="w-4 h-4 text-[#10B981]" />
          Compare ({items.length})
          {/* Iter-196.W (C5) — Winner-aware sticky pill on the rail.
              When the stash has ≥2 items, surface the editorial
              winner inline so visitors see the recommendation
              without having to expand the drawer. */}
          {items.length >= 2 && <CompareWinnerRailPill items={items} />}
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Expanded drawer */}
      {isDrawerOpen && (
        <aside
          data-testid="compare-drawer"
          aria-label="Lender comparison stash"
          className="fixed inset-x-0 bottom-0 z-[110] bg-white border-t-2 border-slate-950 shadow-2xl"
        >
          <div className="max-w-wide mx-auto px-4 md:px-6 lg:px-10 py-3 md:py-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <GitCompareArrows className="w-4 h-4 text-[#10B981]" />
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] text-slate-950">
                  Compare stash
                </p>
                <span className="text-[10px] md:text-xs text-slate-500 font-medium">
                  {items.length} of 4
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="compare-drawer-clear"
                  onClick={clear}
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-rose-600 transition-colors h-9 px-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear all
                </button>
                <button
                  type="button"
                  data-testid="compare-drawer-collapse"
                  onClick={closeDrawer}
                  aria-label="Collapse compare drawer"
                  className="w-9 h-9 grid place-items-center text-slate-500 hover:text-slate-950"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {items.map((it) => (
                <div
                  key={it.slug}
                  data-testid={`compare-drawer-item-${it.slug}`}
                  className="relative border border-slate-200 bg-white px-3 py-2.5 md:py-3"
                >
                  <button
                    type="button"
                    data-testid={`compare-drawer-remove-${it.slug}`}
                    onClick={() => remove(it.slug)}
                    aria-label={`Remove ${it.name} from compare`}
                    className="absolute top-1 right-1 w-7 h-7 grid place-items-center text-slate-500 hover:text-rose-600 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 pr-5">
                    {CATEGORY_LABEL[it.category] || it.category}
                  </p>
                  <p className="font-display font-black text-sm md:text-base text-slate-950 leading-tight mt-1 truncate">
                    {it.name}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600">
                    {it.rating ? (
                      <span className="inline-flex items-center gap-1">
                        <Star className="w-3 h-3 text-[#10B981] fill-[#10B981]" />
                        {Number(it.rating).toFixed(1)}
                      </span>
                    ) : null}
                    <span className="text-slate-300">·</span>
                    <span>{fmtApr(it.min_apr, it.max_apr)}</span>
                  </div>
                </div>
              ))}
              {Array.from({ length: Math.max(0, 4 - items.length) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="hidden md:flex items-center justify-center border border-dashed border-slate-200 bg-[#FAFAFA] py-3 text-[11px] text-slate-500 font-semibold"
                >
                  Add a lender
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                data-testid="compare-drawer-open"
                onClick={openModal}
                disabled={items.length < 2}
                className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold h-11 px-5 transition-colors"
              >
                <GitCompareArrows className="w-4 h-4" />
                Compare side-by-side
              </button>
              {items.length < 2 && (
                <p className="text-[11px] text-slate-500 font-medium">
                  Add 1 more lender to compare
                </p>
              )}
            </div>
          </div>
        </aside>
      )}
    </>
  );
}

export function CompareDrawerModal() {
  const { items, isModalOpen, closeModal, remove } = useCompareStash();
  // Iter-196.X (F-MON-01 / F-CR-02 / F-UX-14) — Wire the Editor's-Pick
  // Apply CTA into the existing QuoteModal so the single highest-intent
  // moment in the funnel (visitor has seen 3 carriers side-by-side,
  // editor picked a winner) actually produces a tracked affiliate
  // handoff instead of being a no-op. Maps the stash category to the
  // QuoteModal's category vocabulary + personalizes the modal headline
  // with the winner name so the handoff feels continuous.
  const { open: openQuoteModal } = useQuoteModal();
  // Iter-196.T (C2) — State-aware premium delta. When the stash is
  // insurance, fetch the state_multipliers map once + render a state
  // picker in the modal header. Selecting a state shows an inline
  // state-adjusted premium next to each carrier's national average.
  // No early return above these hooks — React requires consistent
  // hook order across renders. Hooks first, gate below.
  const [stateCode, setStateCode] = useState("");
  const [stateMultipliers, setStateMultipliers] = useState({});
  const stashCategory = items[0]?.category || null;
  const [, setManifestTick] = useState(0);
  useEffect(() => {
    if (stashCategory === "insurance") ensureProvidersManifest().then(() => setManifestTick((n) => n + 1));
  }, [stashCategory]);
  useEffect(() => {
    if (stashCategory !== "insurance") return;
    if (Object.keys(stateMultipliers).length > 0) return;
    let cancelled = false;
    getInsurancePremiumByState()
      .then((data) => {
        if (cancelled) return;
        setStateMultipliers(data?.state_multipliers || {});
      })
      .catch(() => {
        if (cancelled) return;
      });
    return () => {
      cancelled = true;
    };
  }, [stashCategory, stateMultipliers]);

  if (!isModalOpen || items.length < 2) return null;

  // Iter-123 (L-4) — Export the current comparison snapshot as a CSV
  // so visitors can take it offline (spreadsheet, email to spouse,
  // print). Builds the same column set we render in the modal: name,
  // category, rating, APR low/high, min credit, best_for + the universal
  // detail keys. No external dependencies — a tiny inline String/Blob
  // composition. Filename embeds the date + slug list for traceability.
  //
  // Iter-408 — The CSV columns now MATCH the modal's vertical-aware
  // shape. Previously the export always included "APR low / APR high /
  // Min credit" + a generic "Lender / Provider" header even when the
  // stash was insurance or warranty — those columns were nonsensical
  // for non-loan verticals. Now:
  //   • The first header is the canonical noun for the category
  //     ("Carrier" / "Lender" / "Refinance lender" / "Warranty provider").
  //   • APR low / APR high / Min credit are emitted ONLY for loan-like
  //     categories (auto-loan, refinance), matching the modal's
  //     LOAN_LIKE_CATEGORIES gating.
  //   • Pre-existing crash fixed: DETAILS_COMPARE_KEYS is an OBJECT
  //     keyed by category (not an array), so `.forEach` on it threw
  //     at click-time. Now we look up `DETAILS_COMPARE_KEYS[cat]` and
  //     fall back to [] for unknown categories.
  const downloadCsv = () => {
    const rows = [];
    const cat = items[0]?.category || "carsavr";
    const isLoanLike = LOAN_LIKE_CATEGORIES.has(cat);
    const categoryNounSingular =
      ({
        insurance: "Carrier",
        "auto-loan": "Lender",
        refinance: "Refinance lender",
        warranty: "Warranty provider",
      })[cat] || "Provider";
    const detailKeys = DETAILS_COMPARE_KEYS[cat] || [];
    // Header row — keep order in sync with the modal column order.
    const hdr = [categoryNounSingular, "Vertical", "Editor rating"];
    if (isLoanLike) hdr.push("APR low", "APR high", "Min credit");
    hdr.push("Best for");
    detailKeys.forEach((k) => hdr.push(detailLabel(cat, k)));
    rows.push(hdr);
    items.forEach((it) => {
      const row = [
        it.name || it.slug,
        CATEGORY_LABEL[it.category] || it.category,
        it.rating ? Number(it.rating).toFixed(1) : "",
      ];
      if (isLoanLike) {
        row.push(it.min_apr ?? "", it.max_apr ?? "", it.min_credit ?? "");
      }
      row.push(it.best_for || "");
      detailKeys.forEach((k) => {
        const v = it.details?.[k];
        row.push(v == null ? "" : fmtDetail(cat, k, v));
      });
      rows.push(row);
    });
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const s = String(cell ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    const slugs = items.map((it) => it.slug).join("-");
    a.href = url;
    a.download = `carsavr-${cat}-compare-${date}-${slugs}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    track(events.COMPARE_EXPORT_CSV || "compare_export_csv", {
      category: cat,
      rows: items.length,
    });
  };

  // Iter-196.S (C4 from audit) — Print to PDF. Uses the browser's
  // native print dialog (no library, no extra dependency). A custom
  // @media print stylesheet in index.css transforms the modal into a
  // clean A4/Letter-friendly 1-page comparison sheet by hiding the
  // page chrome, the navigation, the trust-bar/cookie/sticky bars,
  // and the modal scrim, while keeping ONLY the title + table +
  // methodology footer. Visitors can "Save as PDF" from the print
  // dialog on every modern browser (macOS, Windows, iOS, Android).
  //
  // Iter-196.X (F-CR-13 from audit) — Stamp today's date on the
  // modal's `data-print-date` attribute before opening the print
  // dialog so the @media-print ::after footer can interpolate it
  // via attr(data-print-date). Each printed page is now branded
  // ("CarSavr.com — Pricing as of {today} — verify with provider")
  // for FTC truthfulness + brand attribution when the PDF is
  // shared offline.
  const printPdf = () => {
    track(events.COMPARE_EXPORT_PDF || "compare_export_pdf", {
      category: items[0]?.category || "carsavr",
      rows: items.length,
    });
    if (typeof window !== "undefined") {
      const modal = document.querySelector('[data-testid="compare-modal"]');
      if (modal) {
        modal.setAttribute(
          "data-print-date",
          new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        );
      }
      window.print();
    }
  };

  // Iter-196.X (F-MON-01 / F-CR-02 / F-UX-14) — Apply CTA handler.
  // Maps the stash's internal category vocabulary to the QuoteModal's
  // vocabulary (auto-loan → auto-loan-new, etc.), closes the compare
  // modal cleanly, then opens the QuoteModal on the right vertical
  // with a personalized headline + sub that names the winning lender
  // so the handoff feels continuous. Fires a dedicated analytics
  // event so the conversion funnel from "winner shown" → "QuoteModal
  // opened" → "QuoteModal submitted" is measurable end-to-end.
  const STASH_TO_QUOTEMODAL_CATEGORY = {
    insurance: "insurance",
    "auto-loan": "auto-loan-new",
    refinance: "refinance",
    warranty: "warranty",
  };
  const handleWinnerApply = (winner) => {
    if (!winner) return;
    const stashCat = winner.category;
    const quoteCat = STASH_TO_QUOTEMODAL_CATEGORY[stashCat] || "insurance";
    track(events.COMPARE_WINNER_APPLY_CLICK || "compare_winner_apply_click", {
      category: stashCat,
      quote_category: quoteCat,
      winner_slug: winner.slug,
      winner_name: winner.name,
      rating: winner.rating ?? null,
      stash_size: items.length,
    });
    closeModal();
    // Defer the openQuoteModal call to the next frame so the compare-
    // modal close animation isn't fighting the QuoteModal open
    // animation in the same tick. Smoother handoff visually.
    setTimeout(() => {
      openQuoteModal(quoteCat, {
        headline: `Apply with ${winner.name}`,
        sub: `You picked the editor's pick. Drop your ZIP and we'll route you to ${winner.name}'s offer along with 2–3 backup quotes so you have leverage at the table.`,
      });
    }, 50);
  };

  // Iter-107 — Derive the category once. The stash already enforces
  // single-category mixing (iter-93), so `items[0].category` is the
  // canonical category for the whole modal. (Note: `stashCategory`
  // was already computed above for the iter-196.T C2 state-multiplier
  // effect — reuse it here, no need to redeclare.)
  const allSameCategory =
    stashCategory && items.every((it) => it.category === stashCategory);
  const copy = CATEGORY_COPY[stashCategory] || DEFAULT_COPY;
  const showLoanSpecs = LOAN_LIKE_CATEGORIES.has(stashCategory);

  // Iter-107 — Universal spec rows split into two tiers:
  //   • Loan-specific (APR + Min credit) — rendered only for
  //     auto-loan / refinance verticals.
  //   • Always-on (Vertical badge + Editor rating + Best for +
  //     Editor pick) — meaningful for every category.
  // Insurance + warranty verticals get their numeric fields from
  // iter-94's `detailRows` block appended below.
  const LOAN_SPECS = [
    {
      key: "apr",
      label: "APR range",
      render: (it) => fmtApr(it.min_apr, it.max_apr),
    },
    {
      key: "min_credit",
      label: "Min credit",
      render: (it) => (it.min_credit ? `${it.min_credit}+` : "—"),
    },
  ];
  const UNIVERSAL_SPECS = [
    {
      key: "category",
      label: "Vertical",
      render: (it) => CATEGORY_LABEL[it.category] || it.category,
    },
    {
      key: "rating",
      label: "Editor rating",
      render: (it) =>
        it.rating ? (
          <span className="inline-flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 text-[#10B981] fill-[#10B981]" />
            {Number(it.rating).toFixed(1)} / 5
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "best_for",
      label: "Best for",
      render: (it) => it.best_for || "—",
    },
    {
      key: "badge",
      label: "Editor pick",
      render: (it) =>
        it.badge ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-950">
            <Award className="w-3.5 h-3.5 text-[#10B981]" />
            {it.badge}
          </span>
        ) : (
          "—"
        ),
    },
  ];
  // Final SPECS ordering: category badge → loan numerics (if loan) →
  // editorial qualitative. Reads top-to-bottom as: what is it, what
  // do the numbers say, what does our editor say.
  const SPECS = [
    UNIVERSAL_SPECS[0], // category badge
    ...(showLoanSpecs ? LOAN_SPECS : []),
    ...UNIVERSAL_SPECS.slice(1),
  ];

  // Iter-94 — Append per-category detail rows IF the stash is a single
  // category AND at least one stashed lender has populated `details`.
  // Renders fields in the editorially-ranked order from
  // DETAILS_COMPARE_KEYS so the most decision-driving fields come first
  // (avg_monthly_premium for insurance, apr_reduction_avg for refinance,
  // etc.). Empty fields render "—" rather than getting skipped — visitors
  // need to see the gap to know which lender is missing the data.
  //
  // The CompareStash already enforces single-category mixing (iter-93),
  // so `stashCategory` (defined at the top of this function) is
  // well-defined here.
  const detailKeysForCategory =
    (allSameCategory && DETAILS_COMPARE_KEYS[stashCategory]) || [];
  // Only surface detail rows where at LEAST one stashed lender has a value
  // for that key — keeps the modal compact when admins haven't populated
  // every field yet.
  const detailRows = detailKeysForCategory
    .filter((key) =>
      items.some(
        (it) =>
          it.details &&
          it.details[key] !== null &&
          it.details[key] !== undefined &&
          it.details[key] !== "",
      ),
    )
    .map((key) => ({
      key: `detail-${key}`,
      label: detailLabel(key),
      isDetailRow: true,
      render: (it) => {
        const v = it.details ? it.details[key] : null;
        const formatted = fmtDetail(key, v);
        // Iter-196.T (C2) — When the comparison is insurance, the
        // avg_monthly_premium row gets a state-adjusted annotation
        // appended below the national average. Uses the same
        // stateMultipliers map the PartnerRateTable consumes, so
        // there's zero data drift between surfaces.
        const showStateAdj =
          stashCategory === "insurance" &&
          key === "avg_monthly_premium" &&
          stateCode &&
          stateMultipliers[stateCode] != null &&
          typeof v === "number";
        const adjusted = showStateAdj
          ? Math.round(v * stateMultipliers[stateCode])
          : null;
        const pct = showStateAdj
          ? Math.round((stateMultipliers[stateCode] - 1) * 100)
          : 0;
        if (formatted == null) {
          return <span className="text-slate-300">—</span>;
        }
        if (!showStateAdj) {
          return formatted;
        }
        return (
          <div className="flex flex-col gap-1">
            <span>{formatted}</span>
            <span
              data-testid={`compare-row-state-adj-${it.slug}`}
              className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 leading-none"
            >
              {stateCode}: ${adjusted}/mo
              {pct !== 0 && (
                <span className="ml-1 font-normal text-emerald-700/80">
                  ({pct > 0 ? "+" : ""}
                  {pct}%)
                </span>
              )}
            </span>
          </div>
        );
      },
    }));

  // Iter-459 — Insurance compares on real top-level fields (2024 national
  // averages, AM Best); loan-only rows (APR / min credit) are hidden.
  const fmtYr = (v) => (typeof v === "number" ? `$${v.toLocaleString()}/yr` : null);
  const dash = <span className="text-slate-300">—</span>;
  const mult = stateCode ? stateMultipliers[stateCode] : null;
  const premCell = (it, field) => {
    const v = it[field];
    if (typeof v !== "number") return dash;
    if (mult == null) return fmtYr(v);
    const pct = Math.round((mult - 1) * 100);
    return (
      <div className="flex flex-col gap-1">
        <span>{fmtYr(v)}</span>
        <span data-testid={`compare-row-state-adj-${field}-${it.slug}`} className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 leading-none">
          {stateCode}: {fmtYr(statePremium(v, mult))}{pct !== 0 && ` (${pct > 0 ? "+" : ""}${pct}%)`}
        </span>
      </div>
    );
  };
  const insuranceRows = allSameCategory && stashCategory === "insurance"
    ? [
        { key: "ins-full", label: "Full coverage", sub: "2024 national avg", render: (it) => premCell(it, "avg_annual_premium_full") },
        { key: "ins-liability", label: "Liability only", sub: "2024 national avg", render: (it) => premCell(it, "avg_annual_premium_liability") },
        { key: "ins-ambest", label: "AM Best rating", render: (it) => it.am_best || dash },
        { key: "ins-jdpower", label: "J.D. Power score", sub: "out of 1,000", render: (it) => getProviderBySlug(it.slug)?.trust_signals?.jd_power_score ?? dash },
        { key: "ins-naic", label: "NAIC complaint index", sub: "1.0 = industry avg", render: (it) => getProviderBySlug(it.slug)?.trust_signals?.naic_complaint_index ?? dash },
        { key: "ins-source", label: "Rate source", render: (it) => it.data_source || dash },
      ]
    : [];
  const LOAN_ONLY = new Set(["APR range", "Min credit"]);
  const baseSpecs = insuranceRows.length ? SPECS.filter((s) => !LOAN_ONLY.has(s.label)) : SPECS;
  const ALL_SPECS = [...baseSpecs, ...insuranceRows, ...detailRows];

  // Iter-95 — Precompute winner sets per spec row so each cell can decide
  // its own highlight. `winnersByRow` is keyed by spec.key → Set<itemIdx>.
  // Universal specs (apr, rating) use pickUniversalWinners; detail specs
  // use pickDetailWinners which reads DETAILS_WINNER_DIRECTION (lower /
  // higher / true) from the shared lib. Rows that aren't comparable
  // (coverage_type, driver_fit, vertical, best_for, badge) return an
  // empty set and render neutrally.
  const winnersByRow = {};
  for (const spec of ALL_SPECS) {
    if (spec.isDetailRow) {
      const detailKey = spec.key.replace(/^detail-/, "");
      winnersByRow[spec.key] = pickDetailWinners(detailKey, items);
    } else {
      winnersByRow[spec.key] = pickUniversalWinners(spec.key, items);
    }
  }

  return (
    <div
      data-testid="compare-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compare-modal-title"
      className="fixed inset-0 z-[130] grid place-items-center px-4 py-8 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div className="relative w-full min-w-0 sm:min-w-[auto] max-w-prose my-auto">
        <button
          type="button"
          onClick={closeModal}
          data-testid="compare-modal-close"
          aria-label="Close comparison"
          className="absolute top-3 right-3 z-10 w-11 h-11 grid place-items-center bg-white/0 hover:bg-slate-100 text-slate-500 hover:text-slate-950 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="bg-white shadow-2xl">
          <header className="px-7 pt-8 pb-5 border-b border-slate-100">
            <p
              data-testid="compare-modal-eyebrow"
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-2"
            >
              <copy.Icon className="w-3.5 h-3.5" />
              Side-by-side comparison
            </p>
            <h2
              id="compare-modal-title"
              data-testid="compare-modal-title"
              className="font-display font-black text-3xl text-slate-950 leading-[1.05] tracking-tight"
            >
              {items.length} {copy.noun}, {copy.titleSuffix}
            </h2>
            <p
              data-testid="compare-modal-intro"
              className="mt-2 text-sm text-slate-600 leading-relaxed"
            >
              {copy.intro}
            </p>
            {/* Iter-123 (M-4) — Inline affiliate disclosure inside the
                compare drawer so visitors see the partnership disclaimer
                at the exact decision moment, not only on the global
                footer page. Required by FTC 16 CFR § 255 and most
                affiliate-network onboarding reviewers. */}
            <div className="mt-4" data-testid="compare-modal-affiliate-disclosure">
              <AffiliateDisclosure variant="banner" />
            </div>

            {/* Iter-196.W (C5 + C6 from audit) — Editor's pick banner +
                freshness stamp. The banner surfaces the highest-rated
                lender (derived from the same `rating` field the
                PartnerRateTable consumes) with a single-sentence reason,
                so visitors see the recommendation BEFORE scanning the
                spec rows. The freshness stamp below sits flush-right
                signaling that both pricing and the editorial verdict
                are kept current.
                Iter-196.X (F-MON-01) — `onApply` handler is now wired
                into the global QuoteModal: clicking "Apply with {X}"
                closes the compare modal and opens the QuoteModal on
                the right vertical with a personalized headline so the
                handoff feels seamless instead of a no-op CTA. */}
            <CompareWinnerBanner items={items} onApply={handleWinnerApply} />
            <div
              data-testid="compare-modal-freshness-wrap"
              className="mt-3 flex justify-end"
            >
              <CompareFreshnessStamp testidSlug="modal" />
            </div>

            {/* Iter-196.T (C2) — Inline state-aware premium delta.
                When the visitor is comparing insurance carriers AND
                stateMultipliers data is loaded, surface a state
                picker. Selecting a state immediately reflects in the
                per-carrier column header and in the avg_monthly_premium
                detail row (rendered further down). Same rate-multiplier
                data used by the PartnerRateTable since iter-184. */}
            {stashCategory === "insurance" &&
              Object.keys(stateMultipliers).length > 0 && (
                <div
                  data-testid="compare-modal-state-picker-wrap"
                  className="mt-4 inline-flex items-center gap-3 bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2"
                >
                  <MapPin className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2.5} />
                  <label
                    htmlFor="compare-modal-state-select"
                    className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700"
                  >
                    Show state-adjusted premiums
                  </label>
                  <select
                    id="compare-modal-state-select"
                    data-testid="compare-modal-state-picker"
                    value={stateCode}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStateCode(v);
                      if (v) {
                        track(
                          events.COMPARE_STATE_OVERRIDE || "compare_state_override",
                          { state: v, count: items.length },
                        );
                      }
                    }}
                    className="bg-white text-slate-950 text-xs font-bold border border-emerald-300 px-2 h-7"
                  >
                    <option value="">National avg</option>
                    {Object.keys(stateMultipliers)
                      .sort()
                      .map((s) => {
                        const pct = Math.round((stateMultipliers[s] - 1) * 100);
                        const sign = pct > 0 ? "+" : "";
                        const optLabel = `${s} (${sign}${pct}%)`;
                        return (
                          <option key={s} value={s}>{optLabel}</option>
                        );
                      })}
                  </select>
                  {stateCode && (
                    <button
                      type="button"
                      data-testid="compare-modal-state-clear"
                      onClick={() => setStateCode("")}
                      className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 hover:text-emerald-900"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
          </header>

          <CompareMobileCards items={items} specs={ALL_SPECS} onRemove={remove} />
          <div
            data-testid="compare-modal-scroll"
            // Iter-109 — Mobile compare polish:
            //   • `overflow-x-auto` keeps horizontal swipe on phones.
            //   • `relative` is the anchor for the right-edge fade
            //     scroll affordance that subtly tells visitors "swipe
            //     for more". Pure CSS, GPU-only, reduced-motion-safe.
            className="relative overflow-x-auto compare-modal-scroll-shadow hidden md:block"
          >
            <table
              data-testid="compare-modal-table"
              className="min-w-full border-collapse text-sm"
            >
              {/* Iter-109 — Sticky table head. Visitors scrolling a long
                  spec list (8-12 rows once iter-94 detail rows append)
                  used to lose the lender column header within ~2 rows
                  on mobile. `sticky top-0` keeps the "{name} · {category}"
                  header visible during vertical scroll. z-10 sits above
                  body rows but below the modal close button (z-10 there). */}
              <thead className="bg-[#FAFAFA] border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th
                    // Sticky-left makes the spec label travel with the
                    // user during horizontal scroll on mobile. Pairs
                    // with `sticky left-0` on the body cells below.
                    className="sticky left-0 z-20 bg-[#FAFAFA] text-left px-5 py-4 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 w-[140px] border-r border-slate-200"
                  >
                    Spec
                  </th>
                  {items.map((it) => (
                    <th
                      key={it.slug}
                      data-testid={`compare-modal-col-${it.slug}`}
                      className="text-left px-5 py-4 align-top min-w-[160px]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
                            {CATEGORY_LABEL[it.category] || it.category}
                          </p>
                          <p className="font-display font-black text-base text-slate-950 leading-tight">
                            {it.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(it.slug)}
                          aria-label={`Remove ${it.name}`}
                          data-testid={`compare-modal-remove-${it.slug}`}
                          className="text-slate-300 hover:text-rose-600 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_SPECS.map((spec, i) => {
                  // Iter-94 — Insert a subtle category-section divider row
                  // immediately before the first detail row so users can
                  // visually separate universal specs (APR / rating / credit)
                  // from per-vertical specs (premium / mileage / etc.).
                  const isFirstDetailRow =
                    spec.isDetailRow &&
                    (i === 0 || !ALL_SPECS[i - 1].isDetailRow);
                  return (
                    <Fragment key={spec.key}>
                      {isFirstDetailRow && (
                        <tr
                          data-testid="compare-modal-detail-divider"
                          className="bg-[#0A2540]"
                        >
                          <td
                            colSpan={items.length + 1}
                            className="px-5 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981]"
                          >
                            {(stashCategory || "").replace("-", " ")} specifics
                          </td>
                        </tr>
                      )}
                      <tr
                        className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}
                      >
                        <td
                          // Iter-109 — Sticky-left body cell mirrors the
                          // sticky head's spec column. `bg-inherit` keeps
                          // the row's striped color visible behind the
                          // pinned cell (white/FAFAFA alternating).
                          className={`sticky left-0 z-[5] bg-inherit px-5 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 border-r border-slate-100 ${
                            spec.isDetailRow ? "text-[#10B981]" : ""
                          }`}
                        >
                          {spec.label}
                          {spec.sub && (
                            <span className="block mt-0.5 text-[10px] font-medium normal-case tracking-normal text-slate-500">{spec.sub}</span>
                          )}
                        </td>
                        {items.map((it, itemIdx) => {
                          const isWinner = winnersByRow[spec.key]?.has(itemIdx);
                          return (
                            <td
                              key={`${spec.key}-${it.slug}`}
                              data-testid={`compare-row-${spec.key}-${it.slug}`}
                              data-winner={isWinner ? "true" : undefined}
                              className={`px-5 py-3.5 align-top transition-colors ${
                                isWinner
                                  ? "font-semibold text-[#10B981]"
                                  : "text-slate-950"
                              }`}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                {spec.render(it)}
                                {isWinner && (
                                  <CheckCircle2
                                    className="w-3.5 h-3.5 shrink-0"
                                    aria-label="Best in this row"
                                    data-testid={`compare-row-winner-${spec.key}-${it.slug}`}
                                  />
                                )}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className="px-7 py-5 border-t border-slate-100 bg-[#FAFAFA] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Iter-196.Z (F-CR-18 from audit) — Primary winner-apply
                CTA in the modal footer. Solves the audit's "modal CTA
                hierarchy is flat" finding: previously the footer had
                5 equal-weight secondary buttons (Share, Email, CSV,
                Print, See all) and ZERO primary intent CTA. Now the
                footer leads with "Apply with {winner}" — the same
                handoff `handleWinnerApply` already wires from the
                top banner, but available at the bottom of the modal
                where mobile visitors land after scrolling the spec
                table. Tracked separately
                (COMPARE_FOOTER_WINNER_APPLY_CLICK) so the funnel
                dashboard can roll up which position (banner vs.
                footer) converts higher per vertical. */}
            {(() => {
              const footerWinnerResult = pickCompareWinner(items);
              if (!footerWinnerResult) {
                return (
                  <p className="text-xs text-slate-500 leading-relaxed max-w-md">
                    Each column is an editorially-reviewed {stashCategory === "insurance" ? "carrier" : "lender"} — see our{" "}
                    <Link
                      to="/methodology"
                      className="text-slate-950 underline hover:no-underline"
                    >
                      methodology
                    </Link>{" "}
                    for how scores are computed.
                  </p>
                );
              }
              const { winner: footerWinner, reason: footerReason } =
                footerWinnerResult;
              const footerHandleApply = () => {
                track(
                  events.COMPARE_FOOTER_WINNER_APPLY_CLICK ||
                    "compare_footer_winner_apply_click",
                  {
                    category: footerWinner.category,
                    winner_slug: footerWinner.slug,
                    winner_name: footerWinner.name,
                    rating: footerWinner.rating ?? null,
                    stash_size: items.length,
                    reason: footerReason,
                    position: "footer",
                  }
                );
                handleWinnerApply(footerWinner);
              };
              return (
                <div className="flex flex-col gap-1 max-w-md">
                  <button
                    type="button"
                    onClick={footerHandleApply}
                    data-testid="compare-modal-footer-winner-apply"
                    className="inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-white font-bold h-12 px-6 transition-all hover:-translate-y-0.5"
                  >
                    Apply with {footerWinner.name}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Editor&apos;s pick · {footerReason} · soft credit pull
                  </p>
                </div>
              );
            })()}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Iter-96 — Shareable URL. Copies a `/compare?stash=slug1,slug2`
                  link to the clipboard. Recipient lands on /compare → the
                  CompareStashProvider's hydration effect populates the
                  stash and auto-opens this same modal. Viral-loop +
                  ad-campaign asset (run paid ads to pre-built 3-way
                  shortlist landings). */}
              <ShareCompareButton items={items} />
              {/* Iter-123 (L-4) — CSV export so visitors can take the
                  comparison offline (spreadsheet, email, print). Same
                  column order as the modal table for parity. */}
              <button
                type="button"
                onClick={downloadCsv}
                data-testid="compare-modal-export-csv"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-950 text-slate-950 font-bold h-11 px-4 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              {/* Iter-196.S (C4) — Print / PDF export. Uses
                  window.print() + the global @media print CSS in
                  index.css to produce a clean 1-page comparison
                  document. Visitors can "Save as PDF" from the
                  browser's native print dialog on every platform. */}
              <button
                type="button"
                onClick={printPdf}
                data-testid="compare-modal-print-pdf"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-950 text-slate-950 font-bold h-11 px-4 transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print / PDF
              </button>
              <Link
                to="/compare"
                data-testid="compare-modal-see-all"
                onClick={closeModal}
                className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-bold h-11 px-5 transition-colors"
              >
                See all CarSavr comparisons
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
